import { useBrowserNotificationSettings } from '@/hooks/useBrowserNotifications'
import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'
import { HiOutlineBell } from 'react-icons/hi2'

import { Alert, Box, Button, Stack } from '@hivewatch/dark'

import { SettingsGroup } from '@/features/settings'

import { SettingsToggleRow } from './SettingsToggleRow'

/**
 * Opt-in for OS Incident banners. The toggle is the only place permission is
 * requested, since Chrome and Safari require a user gesture. A denial is
 * permanent, so it renders as an explicit blocked message rather than a
 * toggle that silently does nothing.
 */
export const BrowserNotificationsSection = () => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const { isEnabled, isSupported, permission, showTestBanner, toggleEnabled } =
    useBrowserNotificationSettings()

  const isBlocked = permission === 'denied'

  return (
    <SettingsGroup
      colorScheme={colorScheme}
      title={t('settings:rebrand_account_browser_notifications_group_title')}
    >
      <SettingsToggleRow
        colorScheme={colorScheme}
        dataTestId='browser-notifications'
        disabled={!isSupported || isBlocked}
        helperText={t(
          'settings:rebrand_account_browser_notifications_helper_text'
        )}
        isOn={isEnabled}
        label={t('settings:rebrand_account_browser_notifications_label')}
        onChange={() => void toggleEnabled()}
      />

      <Stack gap='12px' w='full'>
        {!isSupported && (
          <Alert
            colorScheme={colorScheme}
            description={t(
              'settings:rebrand_account_browser_notifications_unsupported_description'
            )}
            title={t(
              'settings:rebrand_account_browser_notifications_unsupported_title'
            )}
            variant='warning'
          />
        )}

        {isSupported && isBlocked && (
          <Alert
            colorScheme={colorScheme}
            description={t(
              'settings:rebrand_account_browser_notifications_blocked_description'
            )}
            title={t(
              'settings:rebrand_account_browser_notifications_blocked_title'
            )}
            variant='warning'
          />
        )}

        {isSupported && !isBlocked && isEnabled && (
          <>
            <Alert
              colorScheme={colorScheme}
              description={t(
                'settings:rebrand_account_browser_notifications_behaviour_description'
              )}
              title={t(
                'settings:rebrand_account_browser_notifications_behaviour_title'
              )}
              variant='info'
            />

            {/* OS suppression is invisible to the page, so let the
                operator confirm delivery themselves. */}
            <Box>
              <Button
                colorScheme={colorScheme}
                leftIcon={<HiOutlineBell />}
                onClick={() => void showTestBanner()}
                variant='secondary'
              >
                {t(
                  'settings:rebrand_account_browser_notifications_test_button'
                )}
              </Button>
            </Box>
          </>
        )}
      </Stack>
    </SettingsGroup>
  )
}
